import React, { createContext, useContext } from "react";
import SunnySpinner from "ui/SunnySpinner";

import useSplitIO from "./useSplitIO";
import { getExperimentContext } from "./experimentContextFactory";
import { Experiment, ExperimentFallbackConfig } from "./types";
import { Attributes } from "@splitsoftware/splitio/types/splitio";
import { readTreatmentOverridesFromUrl } from "./experimentConfig";

interface ExperimentContextType {
  getConfig<T extends Experiment>(
    experiment: T,
    attributes?: Attributes,
  ): ExperimentFallbackConfig<T>;
}

export const ExperimentContext = createContext({} as ExperimentContextType);
export const useExperimentContext = () => useContext(ExperimentContext);

// Hoist reading the URL to the context provider element to ensure it's read on page load
readTreatmentOverridesFromUrl();

type ExperimentProviderProps = {
  children?: React.ReactNode;
};

let context: ExperimentContextType;
export function ExperimentProvider(props: ExperimentProviderProps) {
  const { loading, manager, ...clients } = useSplitIO();

  if (loading) {
    return <SunnySpinner fullPage={true} />;
  }

  // We don't want to instantiate the context multiple times, because we don't
  // want the cache to be reset.
  if (!context) {
    context = getExperimentContext({ manager, clients });
  }

  return (
    <ExperimentContext.Provider value={context}>
      {props.children}
    </ExperimentContext.Provider>
  );
}
